/**
 * Shared form-state for the four phone-bezogenen Settings-Sections
 * (Anbieter & Nummer, KI-Modell, Stimme & Sprache, Latenz & Filler).
 *
 * The pre-existing `PhoneSettingsPage` kept this state in a single
 * component; with the flat settings menu the four sections live on
 * separate routes, so the state moves up into a Provider that wraps
 * the entire `<SettingsLayout/>`. The hook returns identical props the
 * existing Tab components already consume, so we don't have to fork
 * their JSX.
 */
import * as React from 'react';
import { useToast } from '../../../components/ui/toast';
import { usePhoneSettings } from '../../../hooks/usePhoneSettings';
import { TELNYX_MEDIA_FORK_URL, TWILIO_CONVERSATION_RELAY_URL } from '../../../lib/voiceAgent';
import { formatCallableError } from '../../../lib/callableError';
import { DEFAULT_FORM_STATE, deepEqual, type FormState, type Tab, withoutSecrets } from './state';

interface PhoneFormContextValue {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  dirtyByTab: Record<Tab, boolean>;
  dirty: boolean;
  loading: boolean;
  /** True once the first server snapshot has been merged into `form`.
   *  Sections should keep their inputs disabled / show a skeleton until
   *  this flips, otherwise users can briefly type into default values
   *  that the late-arriving snapshot then overwrites. */
  hydrated: boolean;
  saving: boolean;
  save: () => Promise<void>;
}

const PhoneFormCtx = React.createContext<PhoneFormContextValue | null>(null);

export function PhoneSettingsProvider({ children }: { children: React.ReactNode }) {
  const { query, mutation } = usePhoneSettings();
  const { toast } = useToast();
  const [form, setForm] = React.useState<FormState>(DEFAULT_FORM_STATE);
  const [hydrated, setHydrated] = React.useState(false);
  const serverData = query.data;

  React.useEffect(() => {
    if (!serverData || hydrated) return;
    const merged = { ...DEFAULT_FORM_STATE, ...(serverData as Partial<FormState>) };
    if (merged.twilio && !merged.twilio.conversationRelayUrl) {
      merged.twilio = { ...merged.twilio, conversationRelayUrl: TWILIO_CONVERSATION_RELAY_URL };
    }
    if (merged.telnyx && !merged.telnyx.mediaForkUrl) {
      merged.telnyx = { ...merged.telnyx, mediaForkUrl: TELNYX_MEDIA_FORK_URL };
    }
    setForm(merged);
    setHydrated(true);
  }, [serverData, hydrated]);

  const dirtyByTab = React.useMemo<Record<Tab, boolean>>(() => {
    if (!serverData)
      return {
        provider: false,
        agent: false,
        llm: false,
        voice: false,
        calendar: false,
        filler: false,
        apikeys: false,
        transfer: false,
      };
    const f = withoutSecrets(form);
    const sec = !!form.secrets?.twilioAuthToken || !!form.secrets?.telnyxApiKey;
    const agentDirty =
      (f.selectedAgentPresetId ?? null) !==
        ((serverData.selectedAgentPresetId as string | null | undefined) ?? null) ||
      (f.introPhrase ?? '') !== ((serverData.introPhrase as string | undefined) ?? '') ||
      (f.outroPhrase ?? '') !== ((serverData.outroPhrase as string | undefined) ?? '') ||
      (f.knowledgeBase ?? '') !== ((serverData.knowledgeBase as string | undefined) ?? '');
    return {
      provider:
        f.telephonyProvider !== serverData.telephonyProvider ||
        !deepEqual(f.twilio, serverData.twilio) ||
        !deepEqual(f.telnyx, serverData.telnyx) ||
        sec,
      agent: agentDirty,
      llm:
        f.llmProvider !== serverData.llmProvider ||
        f.llmModel !== serverData.llmModel ||
        f.llmTemperature !== serverData.llmTemperature ||
        f.llmMaxTokens !== serverData.llmMaxTokens ||
        f.systemPrompt !== serverData.systemPrompt ||
        (f.consultativeMode ?? false) !==
          ((serverData.consultativeMode as boolean | undefined) ?? false) ||
        (f.knowledgeBase ?? '') !== ((serverData.knowledgeBase as string | undefined) ?? '') ||
        (f.introPhrase ?? '') !== ((serverData.introPhrase as string | undefined) ?? '') ||
        (f.outroPhrase ?? '') !== ((serverData.outroPhrase as string | undefined) ?? '') ||
        // Behaviour-rule overrides (5 admin-locked prompt blocks). The
        // textarea's `null` and `''` map to the same "use default" state
        // server-side, so we compare both as "" to avoid false-positive
        // dirty flags right after a fresh load.
        (f.promptCallerIdSecurity ?? '') !==
          ((serverData.promptCallerIdSecurity as string | undefined) ?? '') ||
        (f.promptConversationStyle ?? '') !==
          ((serverData.promptConversationStyle as string | undefined) ?? '') ||
        (f.promptAppointmentWorkflow ?? '') !==
          ((serverData.promptAppointmentWorkflow as string | undefined) ?? '') ||
        (f.promptTicketCreation ?? '') !==
          ((serverData.promptTicketCreation as string | undefined) ?? '') ||
        (f.promptTicketLookup ?? '') !==
          ((serverData.promptTicketLookup as string | undefined) ?? ''),
      voice:
        f.sttProvider !== serverData.sttProvider ||
        f.ttsVoiceId !== serverData.ttsVoiceId ||
        f.ttsModel !== serverData.ttsModel ||
        f.ttsStability !== serverData.ttsStability ||
        f.ttsSimilarityBoost !== serverData.ttsSimilarityBoost ||
        f.language !== serverData.language ||
        (f.voiceAgentMode ?? 'classic') !==
          ((serverData.voiceAgentMode as string | undefined) ?? 'classic') ||
        !deepEqual(f.nativeVoiceAgent ?? null, serverData.nativeVoiceAgent ?? null),
      calendar: false,
      filler:
        f.fillerEnabled !== serverData.fillerEnabled ||
        f.fillerThresholdMs !== serverData.fillerThresholdMs ||
        f.fillerStrategy !== serverData.fillerStrategy ||
        f.maxFillerPerCall !== serverData.maxFillerPerCall ||
        !deepEqual(f.fillerCategories, serverData.fillerCategories),
      apikeys:
        (f.useCentralApiKeys ?? false) !==
        ((serverData.useCentralApiKeys as boolean | undefined) ?? false),
      transfer:
        (f.transferMode ?? 'off') !==
          ((serverData.transferMode as string | undefined) ?? 'off') ||
        (f.transferFallbackTargetId ?? null) !==
          ((serverData.transferFallbackTargetId as string | null | undefined) ?? null) ||
        !deepEqual(f.transferTargets ?? [], serverData.transferTargets ?? []) ||
        !deepEqual(f.trustedCallers ?? [], serverData.trustedCallers ?? []) ||
        (f.promptTransferRules ?? '') !==
          ((serverData.promptTransferRules as string | undefined) ?? ''),
    };
  }, [form, serverData]);

  const dirty = Object.values(dirtyByTab).some(Boolean);

  const save = React.useCallback(async () => {
    try {
      await mutation.mutateAsync(form);
      setForm((prev) => ({ ...prev, secrets: undefined }));
      toast({ title: 'Gespeichert', description: 'Telefon-Einstellungen aktualisiert.' });
    } catch (err) {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: formatCallableError(err),
        variant: 'destructive',
      });
    }
  }, [form, mutation, toast]);

  const value: PhoneFormContextValue = {
    form,
    setForm,
    dirtyByTab,
    dirty,
    loading: query.isLoading || !hydrated,
    hydrated,
    saving: mutation.isPending,
    save,
  };

  return <PhoneFormCtx.Provider value={value}>{children}</PhoneFormCtx.Provider>;
}

export function usePhoneSettingsForm(): PhoneFormContextValue {
  const ctx = React.useContext(PhoneFormCtx);
  if (!ctx) throw new Error('usePhoneSettingsForm must be used within <PhoneSettingsProvider>');
  return ctx;
}
